import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { OnChangeFn, SortingState } from '@tanstack/react-table';
import { toast } from 'sonner';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useAdminApiQuery, useAdminApiMutation } from '@/hooks/use-admin-api-query';
import { qk } from '@/lib/query/keys';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { UsersTable, type User } from '@/components/super-admin/users-table';
import { Pagination } from '@/components/super-admin/pagination';
import { PageContainer } from '@/components/page-container';

const LIMIT = 20;

// Mirrors USER_SORT_KEYS in backend/src/super-admin/super-admin.controller.ts.
// Both sides must normalize identically or the header state describes an order
// the rows are not in.
const USER_SORT_KEYS = ['createdAt', 'email', 'name', 'channelCount'] as const;
type UserSortKey = (typeof USER_SORT_KEYS)[number];

interface UsersResponse {
  data: User[];
  total: number;
  totalPages: number;
}

export function SuperAdminUsersPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // The debounced search timer fires up to 300ms after it was armed, and a sort
  // click inside that window changes the URL without re-arming it. React
  // Router's own functional `setSearchParams(prev => …)` does NOT help: `prev`
  // comes from the render that created the setter, so it is stale too. This ref
  // always holds the live params, so the timer merges into current state.
  // Synced in an effect, not during render — writing a ref during render is a
  // React Compiler error. The effect commits within a frame of the sort click,
  // far inside the 300ms debounce, so the timer still reads current values.
  const searchParamsRef = useRef(searchParams);
  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  const page = Number(searchParams.get('page') ?? '1');
  const search = searchParams.get('search') ?? '';
  // Sort lives in the URL so an operator can share/bookmark a sorted view, which
  // means a stale or hand-edited value can arrive. Normalize with the SAME
  // allowlist and defaults the backend applies (SuperAdminController.getUsers):
  // if the two disagree, the header arrow and aria-sort describe an order the
  // rows are not actually in.
  const rawSortBy = searchParams.get('sortBy');
  const sortBy: UserSortKey = USER_SORT_KEYS.includes(rawSortBy as UserSortKey)
    ? (rawSortBy as UserSortKey)
    : 'createdAt';
  const sortDir: 'asc' | 'desc' = searchParams.get('sortDir') === 'asc' ? 'asc' : 'desc';
  const sorting = useMemo<SortingState>(
    () => [{ id: sortBy, desc: sortDir === 'desc' }],
    [sortBy, sortDir],
  );

  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) return;
    // Same live ref as the debounce, for the same reason.
    const params = new URLSearchParams(searchParamsRef.current);
    params.set('sortBy', first.id);
    params.set('sortDir', first.desc ? 'desc' : 'asc');
    params.set('page', '1');
    setSearchParams(params, { replace: true });
  };

  const [inputValue, setInputValue] = useState(search);

  // Debounce search input -> URL params. The guard matters: without it the
  // effect also runs on mount, and a `?page=3` deep link would bounce itself
  // back to page 1 before the operator saw it.
  useEffect(() => {
    if (inputValue === search) return;
    const timer = setTimeout(() => {
      // Live ref, not the captured `searchParams`: this timer was armed up to
      // 300ms ago and a sort click inside that window must not be reverted.
      const params = new URLSearchParams(searchParamsRef.current);
      if (inputValue) {
        params.set('search', inputValue);
      } else {
        params.delete('search');
      }
      // Both directions change which rows exist, so page 3 of the old result
      // set is meaningless — clearing the box has to reset too.
      params.set('page', '1');
      setSearchParams(params, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync input value when URL search param changes externally
  useEffect(() => {
    setInputValue(search);
  }, [search]);

  const listParams = new URLSearchParams({
    page: String(page),
    limit: String(LIMIT),
    sortBy,
    sortDir,
  });
  if (search) listParams.set('search', search);
  // Pagination AND sort params live in the query key so page/search/sort changes
  // refetch — a key missing the sort would serve stale cached rows;
  // keepPreviousData preserves the old server-pagination UX (previous rows
  // stay visible while the next page loads).
  const usersQuery = useAdminApiQuery<UsersResponse>(
    qk.superAdmin('users', { page, search, sortBy, sortDir }),
    `/super-admin/users?${listParams.toString()}`,
    { placeholderData: keepPreviousData },
  );
  // A `= []` default mints a new array every render, which remounts every cell.
  const users = useMemo(() => usersQuery.data?.data ?? [], [usersQuery.data]);
  const total = usersQuery.data?.total ?? 0;
  const totalPages = usersQuery.data?.totalPages ?? 0;
  const isLoading = usersQuery.isFetching;

  const resetUsers = useAdminApiMutation<
    { deleted: number; skipped: string[] },
    { ids: string[] }
  >(
    ({ ids }) => ({
      path: '/super-admin/users/reset',
      init: { method: 'POST', body: JSON.stringify({ ids }) },
      fallback: 'Delete failed',
    }),
    {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: qk.superAdmin('users') }),
    },
  );

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params, { replace: true });
  };

  // UsersTable awaits onDelete before clearing the selection, so this must
  // resolve after the delete + list invalidation settle (and never reject).
  const handleDelete = async (ids: string[]) => {
    if (ids.length === 0) return;
    try {
      const body = await resetUsers.mutateAsync({ ids });
      toast.success(
        body.deleted === 1 ? 'User deleted' : `${body.deleted} users deleted`,
      );
    } catch (err) {
      toast.error((err as { userMessage?: string }).userMessage ?? 'Delete failed');
    }
  };

  if (usersQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <h2 className="text-xl font-semibold">Failed to load data</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong loading the admin panel. Please try again.
        </p>
        <Button className="mt-4" onClick={() => usersQuery.refetch()}>
          Retry Loading
        </Button>
      </div>
    );
  }

  return (
    <PageContainer variant="fluid" className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Users</h1>
        <Input
          placeholder="Search users..."
          aria-label="Search users"
          className="max-w-[280px]"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
      </div>

      {!isLoading && users.length === 0 ? (
        search ? (
          <div className="flex flex-col items-center justify-center py-16">
            <p className="text-sm text-muted-foreground">
              No users match &quot;{search}&quot;
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <h2 className="text-xl font-semibold">No users yet</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Users will appear here after they sign up and connect their WhatsApp
              Business channels.
            </p>
          </div>
        )
      ) : (
        <>
          <UsersTable
            users={users}
            isLoading={isLoading}
            isDeleting={resetUsers.isPending}
            onDelete={handleDelete}
            sorting={sorting}
            onSortingChange={onSortingChange}
            resetToken={`${page}|${search}|${sortBy}|${sortDir}`}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={LIMIT}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </PageContainer>
  );
}
